import { useCallback, useEffect, useMemo } from "react";
import { queryClient } from "@/client";
import { constants } from "@/constants";
import { routes } from "@/router";
import toast from "react-hot-toast";
import { Trans, useTranslation } from "react-i18next";
import { HiOutlineArrowRight, HiOutlineCheck } from "react-icons/hi2";
import { useNavigate, useSearchParams } from "react-router-dom";

import { orgaInfoQueryKey } from "@/hooks/api/organisation/useGetOrgaInfo";
import { useCheckoutStatus } from "@/hooks/api/subscription/useCheckoutStatus";
import { useSubscriptionStatus } from "@/hooks/api/subscription/useSubscriptionStatus";
import { useAlert } from "@/hooks/useAlert";
import Button from "@/components/atoms/Button";
import Spinner from "@/components/atoms/Spinner";
import FeaturedText from "@/components/molecules/FeaturedText";

const SubscriptionSuccess = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const uid = useMemo(() => Math.random().toString(36).substring(2, 15), []);

  const session_id = useMemo(() => searchParams.get("session_id"), [searchParams]);

  const { data: subscriptionStatus, isLoading: isLoadingSubscriptionStatus } = useSubscriptionStatus(!session_id, uid);

  // This is only for initial subscription upgrade
  const { data: checkoutStatus, maxRetriesReached } = useCheckoutStatus(session_id);

  const isUpgrade = useMemo(
    () => (session_id ? true : !subscriptionStatus?.is_downgrade),
    [session_id, subscriptionStatus],
  );
  const updateType = useMemo(() => (isUpgrade ? "upgrade" : "downgrade"), [isUpgrade]);
  const target = useMemo(
    () => subscriptionStatus?.target || checkoutStatus?.target,
    [subscriptionStatus, checkoutStatus],
  );

  const confirm = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: orgaInfoQueryKey });
    navigate(routes.search);
  }, [navigate]);

  useEffect(() => {
    if (maxRetriesReached) {
      showAlert({
        id: "subscription-error",
        isCloseable: false,
        isSelfClosing: false,
        heading: t("settings.plans.update.error.toast.heading"),
        children: (
          <Trans
            i18nKey="settings.plans.update.error.toast.description"
            components={{
              a: (
                <a
                  href={`mailto:${constants.supportMail}?subject=Unexpected error after buying subscription [${session_id}]`}
                  className={"text-link underline"}
                />
              ),
            }}
          />
        ),
        color: "error",
      });
    }

    return () => {
      toast.dismiss("subscription-error");
    };
  }, [maxRetriesReached]);

  const formattedDate = useCallback((date?: string | null) => {
    if (date) {
      return new Date(date).toLocaleDateString();
    }
  }, []);

  if (isLoadingSubscriptionStatus || (session_id && !checkoutStatus?.completed)) {
    return (
      <div className={"flex flex-grow flex-col items-center justify-center gap-3"}>
        <Spinner size={"lg"} />
        <div className={"grid max-w-80 justify-center gap-2 text-center"}>
          <h2 className="text-xl font-semibold text-primary">{t("settings.plans.update.loading.heading")}</h2>
          <p className="break-words text-sm text-tertiary">{t("settings.plans.update.loading.description")}</p>
        </div>
      </div>
    );
  }

  return (
    <FeaturedText
      featuredIcon={{ icon: HiOutlineCheck, color: "success", size: "xl" }}
      heading={t(`settings.plans.update.${updateType}.success.heading`, {
        plan: target?.name,
        date: formattedDate(subscriptionStatus?.start_date),
      })}
      body={t(`settings.plans.update.${updateType}.success.description`)}
    >
      <div className={"pt-3"}>
        <Button
          label={t("settings.plans.start")}
          onClick={() => confirm()}
          trailingIcon={<HiOutlineArrowRight className={"size-5"} />}
        />
      </div>
    </FeaturedText>
  );
};

export default SubscriptionSuccess;
